// Client-side platform-admin gate. Mirrors functions/src/support/platformAdmins.ts
// — keep the root allowlist in sync.
//
// Two ways in, matching the server's requirePlatformAdmin:
//   1. the root allowlist (tagnologys@gmail.com, verified email) — synchronous;
//   2. the `platformAdmin` custom claim, which padminGrantStaff sets for
//      Tagnologys staff — needs the ID token, so it is async.
//
// This gates the /padmin UI so non-admins can't even open the console. It is a
// convenience/UX gate only; the REAL security is enforced server-side (every
// padmin callable + Firestore rules check the same allowlist / claim).
import { useEffect, useState } from 'react';
import type { User } from 'firebase/auth';

export const PLATFORM_ADMIN_EMAILS: readonly string[] = ['tagnologys@gmail.com'];

/** True when the signed-in user's verified email is on the root allowlist. */
export function isPlatformAdminUser(user: User | null | undefined): boolean {
  const email = user?.email?.trim().toLowerCase();
  if (!email) return false;
  // Google sign-in always yields a verified email; guard anyway.
  if (user?.emailVerified === false) return false;
  return PLATFORM_ADMIN_EMAILS.includes(email);
}

export type StaffRole = 'superadmin' | 'support' | 'finance' | null;

export interface AdminAccess {
  /** null while the token is still being read. */
  allowed: boolean | null;
  isRoot: boolean;
  role: StaffRole;
}

/**
 * Root allowlist OR the platformAdmin custom claim. Forces a token refresh once
 * so a claim granted moments ago is picked up without a sign-out/sign-in.
 */
export function useAdminAccess(user: User | null | undefined): AdminAccess {
  const isRoot = isPlatformAdminUser(user);
  // Only the ASYNC claim lookup is kept in state. Root access must never be,
  // because state lags a render behind: while auth resolves, `user` is null,
  // the effect writes allowed:false, and the route guard would redirect on that
  // stale value the instant the real user arrives — locking the root admin out
  // of the console entirely.
  const [claim, setClaim] = useState<{ allowed: boolean; role: StaffRole } | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Nothing to look up while auth is resolving, or when root already decides it.
    if (!user || isRoot) { setClaim(null); return; }

    user.getIdTokenResult(true)
      .then((tok) => {
        if (cancelled) return;
        const claimOk = tok.claims.platformAdmin === true;
        const role = (typeof tok.claims.staffRole === 'string' ? tok.claims.staffRole : null) as StaffRole;
        setClaim({ allowed: claimOk, role: claimOk ? (role ?? 'support') : null });
      })
      .catch(() => { if (!cancelled) setClaim({ allowed: false, role: null }); });

    return () => { cancelled = true; };
  }, [user, isRoot]);

  // Derived synchronously — no render lag.
  if (isRoot) return { allowed: true, isRoot: true, role: 'superadmin' };
  if (!user) return { allowed: false, isRoot: false, role: null };
  if (claim === null) return { allowed: null, isRoot: false, role: null }; // still checking
  return { allowed: claim.allowed, isRoot: false, role: claim.role };
}
